import React, { useEffect, useRef, useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import { classNames } from "../../helpers/classNames";
import { usePopper } from "react-popper";
import { elementResize, getElementPosition } from "../../helpers";

const DatePicker = ({
  label,
  required,
  value,
  dateFormat = "DD/MM/YYYY",
  labelClasses,
  renge = false,
  onChange = () => {},
  showShortcuts = false,
  placeholder,
  errorType,
  errorText,
  className,
  resetTime = true,
  minDate,
  isDisabled = false,
  position = "absolute",
}) => {
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [dateInputProperty, setDateInputProperty] = useState(null);
  const referenceRef = useRef(null);

  useEffect(() => {
    setDate({
      startDate: value?.startDate || null,
      endDate: value?.endDate || null,
    });
  }, [value]);

  function resetTimeHandler(date) {
    console.log(date, "date");
    const resetDate = new Date(date);
    resetDate.setHours(0, 0, 0, 0);
    return resetDate;
  }

  const handleChange = (newDate) => {
    const { startDate, endDate } = newDate;
    if (resetTime && startDate && endDate) {
      const start = resetTimeHandler(startDate);
      const end = resetTimeHandler(endDate);
      setDate({ startDate: start, endDate: end });
      onChange({ startDate: start, endDate: end });
    } else {
      setDate(newDate);
      onChange(newDate);
    }
  };

  useEffect(() => {
    const refEl = referenceRef.current;
    if (refEl) {
      const input = refEl.querySelector(".datepicker");
      const popup = input.lastChild;
      getElementPosition(input, setDateInputProperty);
      const cleanup = elementResize({ current: popup }, (val) => {
        setDateInputProperty((pre) => ({ ...pre, ...val }));
      });
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, []);

  useEffect(() => {
    if (position !== "fixed") {
      return;
    }
    const refEl = referenceRef.current;
    if (refEl) {
      const input = refEl.querySelector(".datepicker");
      const popup = input.lastChild;

      popup.style.position = "fixed";
      popup.style.width = "fit-content";
      popup.style.top = `${dateInputProperty?.bottom || 0}px`;
      popup.style.left = `${dateInputProperty?.left || 0}px`;
      popup.style.zIndex = 9999;

      // Check if popup is within viewport
      const popupRect = popup.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const isFullyVisible =
        popupRect.top >= 0 &&
        popupRect.left >= 0 &&
        popupRect.bottom <= viewportHeight &&
        popupRect.right <= viewportWidth;

      if (!isFullyVisible) {
        popup.style.top = `${dateInputProperty?.height || 0}px`;
      }
    }
  }, [JSON.stringify(dateInputProperty), position]);

  return (
    <div className="w-full relative space-y-1">
      {label && (
        <div className={classNames("text-xs font-medium text-text-disabled mb-1 ", labelClasses)}>
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-0.5 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <div className="relative" ref={referenceRef}>
        <Datepicker
          displayFormat={dateFormat}
          primaryColor={"blue"}
          value={date}
          {...(position !== "fixed" && { popoverDirection: "down" })}
          asSingle={!renge}
          useRange={renge}
          onChange={handleChange}
          showShortcuts={showShortcuts}
          popoverDirection={"down"}
          placeholder={placeholder || "Select date range"}
          containerClassName={" relative datepicker"}
          minDate={minDate}
          disabled={isDisabled}
          classNames={"datepicker"}
          inputClassName={classNames(
            "w-full h-10 p-2 text-sm  border border-text-secondary/50 rounded-md focus:outline-none focus:border-text-disabled focus:ring-0 placeholder:text-text-disabled",
            className
          )}
        />
      </div>
      {errorType && errorType === "danger" && (
        <div className="text-xs text-red-600">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs text-amber-600">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs text-green-600">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs text-sky-600">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
